import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
﻿import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  formatBrandMoney,
  getBrandDefaultCurrency,
  getBrandCurrencySuffix,
  getBrandLocale,
  getBrandDatePickerLocale,
} from '../../shared/utils/brandCurrency';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle, Search, Plus, Edit, Trash2, CheckCircle, AlertTriangle, Calendar,
  ChevronDown, ChevronUp, Upload, Download, FileSpreadsheet, FileOutput, Filter,
  Eye, X, XCircle, CheckSquare, Square, LayoutGrid, Layers, Users, DollarSign,
  FileText, Clock, Shield, TrendingUp, Scale, Tag,
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import { ensureStatusBeforeActions } from '../../shared/utils/erpListColumnOrder';
import { useQawaemTableColumnVisibility, withQawaemActionsColumn, countVisibleTableCols } from '../../shared/hooks/useQawaemTableColumnVisibility';
import { useSettings } from '../../core/contexts/SettingsContext';
import { printListPdfWithInvoiceTemplate } from './utils/templateListPdfExport';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import DatePicker from 'react-datepicker';
import { registerLocale } from 'react-datepicker';
import arLocale from 'date-fns/locale/ar-SA';
import 'react-datepicker/dist/react-datepicker.css';
import '../../shared/styles/GPSTrackingDatePicker.css';
import '../../core/styles/global-dropdowns.css';
import FacturationViewModeToggle from './components/FacturationViewModeToggle';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import FacturationListPageLayout, {
  facturationTableHeadClass,
  facturationTableScrollClass,
} from './components/FacturationListPageLayout';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import useFacturationModalBodyLock from './utils/useFacturationModalBodyLock';
import { useFacturationConfig } from './useFacturationConfig';
import FacturationListPaginationBar from './components/FacturationListPaginationBar';
import {
  useFacturationListPagination,
  useFacturationListViewMode,
} from './facturationListPageUtils';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';

registerLocale('ar', arLocale);
registerLocale('ar-SA', arLocale);

function ImpayeEditableCell({
  recordId,
  field,
  value,
  className = 'text-white font-semibold',
  isEditing,
  editValue,
  onEditValueChange,
  onStartEdit,
  onSave,
  onCancel,
  clickToEditTitle,
  readOnly = true,
  displayValue,
}) {
  const inputRef = useRef(null);
  const skipBlurSaveRef = useRef(false);

  useEffect(() => {
    if (readOnly || !isEditing || !inputRef.current) return;
    const el = inputRef.current;
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      el.focus();
      const len = el.value.length;
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [isEditing, readOnly]);

  if (readOnly) {
    return <span className={className}>{(displayValue ?? value) || '-'}</span>;
  }

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => onEditValueChange(e.target.value)}
        onBlur={(e) => {
          if (skipBlurSaveRef.current) {
            skipBlurSaveRef.current = false;
            return;
          }
          onSave(recordId, field, e.target.value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            skipBlurSaveRef.current = true;
            onSave(recordId, field, e.currentTarget.value);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            onCancel();
          }
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        className={`${className} bg-transparent border-none outline-none focus:outline-none w-full px-0`}
        style={{ caretColor: 'white' }}
        autoComplete="off"
        spellCheck={false}
      />
    );
  }


  return (
    <span
      role="button"
      tabIndex={0}
      onClick={() => onStartEdit(recordId, field, value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onStartEdit(recordId, field, value);
        }
      }}
      className={`${className} cursor-pointer`}
      title={clickToEditTitle}
    >
      {value || value === 0 ? value : '-'}
    </span>
  );
}

const IMPAYE_MODAL_SHELL = 'bg-white border border-gray-200 backdrop-blur-lg rounded-2xl';
const IMPAYE_MODAL_SHADOW = {
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12), 0 0 0 1px rgba(15, 23, 42, 0.06)',
};
const IMPAYE_FIELD =
  'w-full bg-white border border-gray-300 rounded-xl px-4 py-2.5 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all';
const IMPAYE_LABEL = 'block text-gray-600 text-sm mb-2 font-medium';
const IMPAYE_SECTION = 'bg-gray-50 rounded-xl border border-gray-200 overflow-hidden';

function getAgingBadge(bucket) {
  const colors = {
    '0-30': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    '31-60': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    '61-90': 'bg-red-500/20 text-red-400 border-red-500/30',
    '90+': 'bg-red-600/20 text-red-500 border-red-600/30',
  };
  return (
    <span className={`px-3 py-1.5 rounded-full text-xs font-medium border ${colors[bucket] || 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
      {bucket || '-'}
    </span>
  );
}

function getOverdueBadge(days) {
  const daysNum = parseInt(days || 0, 10);
  if (daysNum <= 0) return <span className="text-white/50 text-xs">—</span>;
  if (daysNum < 30) return <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/30">{daysNum}j</span>;
  if (daysNum < 60) return <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-orange-500/20 text-orange-400 border-orange-500/30"><AlertTriangle size={14} className="inline mr-1" />{daysNum}j</span>;
  return <span className="px-3 py-1.5 rounded-full text-xs font-medium border bg-red-500/20 text-red-400 border-red-500/30"><AlertCircle size={14} className="inline mr-1" />{daysNum}j</span>;
}

const ImpayesCreancesPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.facturation.pages.impayesCreancesPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const datePickerLocale = getBrandDatePickerLocale();
  const fmtDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString(dateLocale);
    } catch {
      return dateStr;
    }
  };
  const { isDashboardLightMode } = useGlobalTheme();
  const qawaem = isSaudiBrand();
  const { settings } = useSettings();
  const facturationConfig = useFacturationConfig();

  const statusOptions = useMemo(() => [
    { value: 'ouvert', label: p('status_open'), icon: AlertCircle, style: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
    { value: 'partiel', label: p('status_partial'), icon: Clock, style: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
    { value: 'recouvré', label: p('status_recovered'), icon: CheckCircle, style: 'bg-green-500/20 text-green-400 border-green-500/30' },
    { value: 'litige', label: p('status_dispute'), icon: Scale, style: 'bg-red-500/20 text-red-400 border-red-500/30' },
  ], [t]);

  const getStatusLabel = useCallback((value) => {
    const opt = statusOptions.find((s) => s.value === value);
    return opt ? opt.label : value;
  }, [statusOptions]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dossiers, setDossiers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecords, setSelectedRecords] = useState([]);

  const overdueThresholdDays = facturationConfig.overdueThresholdDays ?? 30;

  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingRecord, setViewingRecord] = useState(null);
  useFacturationModalBodyLock(showModal, showViewModal);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [dossierFormTab, setDossierFormTab] = useState('dossier');
  const dossierFormTabs = useMemo(
    () => [
      { id: 'dossier', label: p('section_dossier_info'), icon: AlertCircle, iconColor: 'text-red-400' },
      { id: 'followup', label: p('section_followup'), icon: Clock, iconColor: 'text-orange-400' },
    ],
    [p],
  );
  const [formData, setFormData] = useState({
    reference_number: '',
    invoice_id: '',
    invoice_number: '',
    client_id: '',
    client_name: '',
    amount_due: '',
    due_date: '',
    days_overdue: '',
    aging_bucket: '',
    status: '',
    last_reminder_date: '',
    notes: '',
  });
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [isDateRangeOpen, setIsDateRangeOpen] = useState(false);
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_dossiers');
  const [quickFilter, setQuickFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isStatusFilterOpen, setIsStatusFilterOpen] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const { filters, setFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'ملف المتأخرات' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'ملف' });

  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const dateRangeRef = useRef(null);
  const statusFilterRef = useRef(null);

  useEffect(() => { loadDossiers(); }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) setShowImportDropdown(false);
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) setShowExportDropdown(false);
      if (dateRangeRef.current && !dateRangeRef.current.contains(event.target)) setIsDateRangeOpen(false);
      if (statusFilterRef.current && !statusFilterRef.current.contains(event.target)) setIsStatusFilterOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadDossiers = async () => {
    try {
      const response = await localApiAdapter.get('/facturation/impayes-creances');
      const rows = response?.data || response || [];
      setDossiers(sortRecordsNewestFirst(Array.isArray(rows) ? rows : []));
    } catch (error) {
      console.error('Error loading dossiers:', error);
      setDossiers([]);
    }
  };

  const stats = useMemo(() => {
    const clientSet = new Set(dossiers.map((d) => d.client_id || d.client_name).filter(Boolean));
    const threshold = overdueThresholdDays;
    const threshold2 = threshold * 2;
    return {
      total: dossiers.length,
      montantDu: dossiers.reduce((sum, d) => sum + (parseFloat(d.amount_due) || 0), 0),
      over30: dossiers.filter((d) => (parseInt(d.days_overdue, 10) || 0) > threshold).length,
      over60: dossiers.filter((d) => (parseInt(d.days_overdue, 10) || 0) > threshold2).length,
      clients: clientSet.size,
    };
  }, [dossiers, overdueThresholdDays]);

  const columnFilterRules = useMemo(() => [
    textColumnRule('reference_number', (row) => row.reference_number),
    textColumnRule('client_name', (row) => row.client_name),
    textColumnRule('invoice_number', (row) => row.invoice_number),
    textColumnRule('amount_due', (row) => String(row.amount_due ?? '')),
    textColumnRule('due_date', (row) => row.due_date),
    textColumnRule('days_overdue', (row) => String(row.days_overdue ?? '')),
    exactColumnRule('status', (row) => row.status),
  ], []);

  const filteredDossiers = useMemo(() => {
    const base = dossiers.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (dateRange.start || dateRange.end) {
        const dueDate = d.due_date ? new Date(d.due_date) : null;
        if (!dueDate) return false;
        if (dateRange.start) {
          const startOfDay = new Date(dateRange.start);
          startOfDay.setHours(0, 0, 0, 0);
          if (dueDate < startOfDay) return false;
        }
        if (dateRange.end) {
          const endOfDay = new Date(dateRange.end);
          endOfDay.setHours(23, 59, 59, 999);
          if (dueDate > endOfDay) return false;
        }
      }
      const q = searchTerm.toLowerCase();
      if (q && !(
        d.reference_number?.toLowerCase().includes(q) ||
        d.client_name?.toLowerCase().includes(q) ||
        d.invoice_number?.toLowerCase().includes(q)
      )) return false;
      if (quickFilter === 'over30' && (parseInt(d.days_overdue, 10) || 0) <= overdueThresholdDays) return false;
      if (quickFilter === 'over60' && (parseInt(d.days_overdue, 10) || 0) <= overdueThresholdDays * 2) return false;
      return true;
    });
    return applyColumnFilters(base, filters, columnFilterRules);
  }, [dossiers, statusFilter, dateRange, searchTerm, quickFilter, filters, columnFilterRules, overdueThresholdDays]);

  const dossiersFooter = useMemo(() => {
    const clientSet = new Set();
    let montantDu = 0;
    filteredDossiers.forEach((d) => {
      montantDu += parseFloat(d.amount_due) || 0;
      if (d.client_id || d.client_name) clientSet.add(d.client_id || d.client_name);
    });
    return { nbClients: clientSet.size, montantDu };
  }, [filteredDossiers]);

  const {
    pageSize: facturationPageSize,
    listPage: facturationListPage,
    setListPage: setFacturationListPage,
    pagedItems,
    totalItems: facturationListTotal,
  } = useFacturationListPagination(filteredDossiers);
  const [viewMode, setViewMode] = useFacturationListViewMode(facturationConfig);

  const filteredRecordIds = useMemo(() => filteredDossiers.map((r) => r.id), [filteredDossiers]);

  const allRowsSelected = filteredRecordIds.length > 0 && filteredRecordIds.every((id) => selectedRecords.includes(id));
  const someRowsSelected = selectedRecords.length > 0 && !allRowsSelected;

  const resetForm = () => {
    setFormData({
      reference_number: '',
      invoice_id: '',
      invoice_number: '',
      client_id: '',
      client_name: '',
      amount_due: '',
      due_date: '',
      days_overdue: '',
      aging_bucket: '',
      status: 'ouvert',
      last_reminder_date: '',
      notes: '',
    });
    setEditingRecord(null);
    setShowModal(false);
    setDossierFormTab('dossier');
  };

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        amount_due: parseFloat(formData.amount_due) || 0,
      };
      if (editingRecord) {
        await localApiAdapter.put(`/facturation/impayes-creances/${editingRecord.id}`, submitData);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post('/facturation/impayes-creances', submitData);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      await loadDossiers();
    } catch (error) {
      console.error('Error saving:', error);
      notifyError(showNotify, p('error_save', { defaultValue: 'تعذّر حفظ الملف' }));
    }
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setFormData({
      reference_number: record.reference_number || '',
      invoice_id: record.invoice_id || '',
      invoice_number: record.invoice_number || '',
      client_id: record.client_id || '',
      client_name: record.client_name || '',
      amount_due: record.amount_due != null ? String(record.amount_due) : '',
      due_date: record.due_date || '',
      days_overdue: record.days_overdue != null ? String(record.days_overdue) : '',
      aging_bucket: record.aging_bucket || '',
      status: record.status || 'ouvert',
      last_reminder_date: record.last_reminder_date || '',
      notes: record.notes || '',
    });
    setDossierFormTab('dossier');
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm', { defaultValue: 'هل تريد حذف هذا الملف؟' }))) {
      try {
        await localApiAdapter.delete(`/facturation/impayes-creances/${id}`);
        notifyDeleted(showNotify, entityLabel);
        await loadDossiers();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('delete_error', { defaultValue: 'تعذّر الحذف' }));
      }
    }
  };

  const handleBulkDelete = async () => {
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`/facturation/impayes-creances/${id}`)));
        setSelectedRecords([]);
        notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
        await loadDossiers();
      } catch (error) {
        console.error('Error:', error);
        notifyError(showNotify, p('bulk_delete_error', { defaultValue: 'تعذّر الحذف' }));
      }
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await localApiAdapter.put(`/facturation/impayes-creances/${id}`, { status: newStatus });
      notifyUpdated(showNotify, entityLabel);
      await loadDossiers();
    } catch (error) {
      console.error('Error updating status:', error);
      notifyError(showNotify, p('error_save', { defaultValue: 'تعذّر التحديث' }));
    }
  };

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) setSelectedRecords([]);
    else setSelectedRecords(filteredRecordIds);
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) => (prev.includes(id) ? prev.filter((rId) => rId !== id) : [...prev, id]));
  };

  const startEditing = (recordId, field, currentValue) => {
    setEditingCell({ recordId, field });
    setEditValue(currentValue || '');
  };

  const cancelEditing = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveInlineEdit = async (recordId, field, value) => {
    const record = dossiers.find((d) => d.id === recordId);
    if (!record || String(value ?? '').trim() === String(record[field] ?? '').trim()) {
      cancelEditing();
      return;
    }
    try {
      await localApiAdapter.put(`/facturation/impayes-creances/${recordId}`, { [field]: value });
      notifyUpdated(showNotify, entityLabel);
      await loadDossiers();
      cancelEditing();
    } catch (error) {
      console.error('Error saving inline edit:', error);
      notifyError(showNotify, p('error_save', { defaultValue: 'تعذّر التحديث' }));
      cancelEditing();
    }
  };

  const exportHeaders = useMemo(() => [
    { key: 'reference_number', label: p('export_col_reference') },
    { key: 'client_name', label: p('col_client') },
    { key: 'invoice_number', label: p('col_invoice') },
    { key: 'amount_due', label: p('col_amount_due') },
    { key: 'due_date', label: p('col_due_date') },
    { key: 'days_overdue', label: p('export_col_overdue_days') },
    { key: 'aging_bucket', label: p('col_aging') },
    { key: 'status', label: p('col_status') },
  ], [t]);

  const handleExportCSV = () => {
    const dataToExport = selectedRecords.length > 0
      ? dossiers.filter((d) => selectedRecords.includes(d.id))
      : filteredDossiers;
    exportToCSV(dataToExport, exportHeaders, `impayes_creances_${new Date().toISOString().split('T')[0]}.csv`);
    setShowExportDropdown(false);
  };

  const handleExportExcel = async () => {
    const dataToExport = selectedRecords.length > 0
      ? dossiers.filter((d) => selectedRecords.includes(d.id))
      : filteredDossiers;
    await exportToExcel(dataToExport, exportHeaders, `impayes_creances_${new Date().toISOString().split('T')[0]}.xlsx`, p('export_sheet_name'));
    setShowExportDropdown(false);
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      const dataToExport = selectedRecords.length > 0
        ? dossiers.filter((d) => selectedRecords.includes(d.id))
        : filteredDossiers;
      await printListPdfWithInvoiceTemplate({
        settings,
        rows: dataToExport,
        columns: [
          { label: p('col_reference'), value: (d) => d.reference_number },
          { label: p('col_client'), value: (d) => d.client_name },
          { label: p('col_invoice'), value: (d) => d.invoice_number },
          { label: p('col_amount_due'), align: 'right', value: (d) => formatBrandMoney(d.amount_due || 0) },
          { label: p('col_due_date'), value: (d) => d.due_date },
          { label: p('col_overdue'), value: (d) => p('days_suffix', { count: d.days_overdue || 0 }) },
          { label: p('col_status'), value: (d) => getStatusLabel(d.status) },
        ],
        listExportTitle: p('export_list_title'),
        clientTitle: p('export_client_title'),
        printDocumentTitle: p('export_document_title', { date: new Date().toISOString().split('T')[0] }),
      });
      setShowExportDropdown(false);
    } catch (error) {
      console.error('PDF export error:', error);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const listStats = useMemo(() => [
    { key: 'total', title: p('stat_total_dossiers'), value: stats.total, icon: FileText, color: 'from-blue-500 to-blue-600' },
    { key: 'amount', title: p('stat_amount_due'), value: formatBrandMoney(stats.montantDu), icon: DollarSign, color: 'from-orange-500 to-orange-600' },
    { key: 'over30', title: p('stat_over_30'), value: stats.over30, icon: AlertTriangle, color: 'from-yellow-500 to-yellow-600' },
    { key: 'over60', title: p('stat_over_60'), value: stats.over60, icon: AlertCircle, color: 'from-red-500 to-red-600' },
    { key: 'clients', title: p('stat_clients'), value: stats.clients, icon: Users, color: 'from-purple-500 to-purple-600', className: 'max-lg:hidden' },
  ], [stats, p]);

  const qawaemStatusOptions = useMemo(() => [
    { value: 'all', label: p('all_statuses') },
    { value: 'active', label: p('status_open') || p('status_values.active') || p('dist_active') },
    { value: 'inactive', label: p('status_closed') || p('status_values.inactive') || p('dist_inactive') },
  ], [p]);

  const qawaemStatusLabel = statusFilter === 'all'
    ? (p('status_filter') || p('status_label') || p('col_status'))
    : (qawaemStatusOptions.find((o) => o.value === statusFilter)?.label || p('status_filter'));
  const qawaemBaseColumns = useMemo(() => ensureStatusBeforeActions([
    { key: 'reference_number', label: p('col_reference'), required: true },
    { key: 'client_name', label: p('col_client') },
    { key: 'invoice_number', label: p('col_invoice') },
    { key: 'amount_due', label: p('col_amount_due') },
    { key: 'due_date', label: p('col_due_date') },
    { key: 'days_overdue', label: p('col_overdue') },
    { key: 'status', label: p('col_status') },
  ]), [p]);
  const { handleToggleColumn, showCol, pickerColumns, visibleColumnCount } = useQawaemTableColumnVisibility(qawaemBaseColumns);
  const qawaemColumns = useMemo(
    () => withQawaemActionsColumn(pickerColumns, p('col_actions')),
    [pickerColumns, p],
  );
  const visibleTableColCount = useMemo(
    () => countVisibleTableCols(qawaemBaseColumns, showCol, { extra: 1 }),
    [qawaemBaseColumns, showCol],
  );
  return (
    <div className="space-y-3 employees-container">
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />

<FacturationListPageLayout
        footer={(
          <FacturationListPaginationBar
            totalItems={facturationListTotal}
            rawPage={facturationListPage}
            onPageChange={setFacturationListPage}
            pageSize={facturationPageSize}
          />
        )}
      className="space-y-3"
      containerClassName="employees-container"
      title={p('title')}
      subtitle={p('subtitle')}
      icon={AlertCircle}
      stats={listStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder')}
      columns={qawaemColumns}
      visibleColumnCount={visibleColumnCount}
      totalColumnCount={qawaemColumns.length}
      onCreate={openNewModal}
      createLabel={p('new_dossier')}
      headerActions={null}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: p('import_label') || p('import') || t('common.import'),
        exportLabel: p('export_label') || t('common.export'),
        generatingLabel: p('generating') || t('common.loading'),
        isGeneratingPDF: typeof isGeneratingPDF !== 'undefined' ? isGeneratingPDF : false,
        onImportCSV: typeof handleImportCSV !== 'undefined' ? handleImportCSV : () => setShowImportDropdown(false),
        onImportExcel: typeof handleImportExcel !== 'undefined' ? handleImportExcel : () => setShowImportDropdown(false),
        onImportJSON: typeof handleImportJSON !== 'undefined' ? handleImportJSON : () => {},
        onImportXML: typeof handleImportXML !== 'undefined' ? handleImportXML : () => {},
        onImportTXT: typeof handleImportTXT !== 'undefined' ? handleImportTXT : () => {},
        onExportPDF: typeof handleExportPDF !== 'undefined' ? handleExportPDF : () => {},
        onExportExcel: typeof handleExportExcel !== 'undefined' ? handleExportExcel : () => {},
        onExportCSV: typeof handleExportCSV !== 'undefined' ? handleExportCSV : () => {},
        onExportJSON: typeof handleExportJSON !== 'undefined' ? handleExportJSON : () => {},
        onExportXML: typeof handleExportXML !== 'undefined' ? handleExportXML : () => {},
        onExportHTML: typeof handleExportHTML !== 'undefined' ? handleExportHTML : () => {},
        onExportTXT: typeof handleExportTXT !== 'undefined' ? handleExportTXT : () => {},
      }}
      showAdvancedOptions={showAdvancedOptions}
      onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
      advancedOptionsLabel={p('advanced_options')}
      advancedOptionsContent={(
        <div className="space-y-6 pt-6" />
      )}
      controlsStrip={(
<div className="facturation-list-controls-strip p-4 border-b border-white/10">
          <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
            {viewMode === 'cards' ? (
              <div className="relative min-w-0 flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                <input type="text" placeholder={p('search_placeholder')} value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
            ) : null}
            <div className="facturation-list-toolbar ml-auto flex shrink-0 items-center gap-2 flex-wrap">
              <FacturationViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} isLight={isDashboardLightMode} />

              <div className="relative" ref={statusFilterRef}>
                <button type="button" onClick={() => setIsStatusFilterOpen(!isStatusFilterOpen)} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Filter size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('status_label')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isStatusFilterOpen ? 'rotate-180' : ''}`} />
                </button>
                {isStatusFilterOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[200px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)' }}>
                    <div className="p-2">
                      <button type="button" onClick={() => { setStatusFilter('all'); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === 'all' ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                        <Filter size={16} className="text-white/50" /><span className="text-sm font-medium">{p('all_statuses')}</span>
                      </button>
                      {statusOptions.map((opt) => (
                        <button key={opt.value} type="button" onClick={() => { setStatusFilter(opt.value); setIsStatusFilterOpen(false); }} className={`w-full px-4 py-2.5 text-left transition-colors flex items-center gap-3 rounded-xl ${statusFilter === opt.value ? 'bg-blue-500/20 text-white' : 'text-white/90 hover:bg-blue-500/20'}`}>
                          <opt.icon size={16} /><span className="text-sm font-medium">{opt.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="relative" ref={dateRangeRef}>
                <button type="button" onClick={() => { setIsDateRangeOpen(!isDateRangeOpen); setShowImportDropdown(false); setShowExportDropdown(false); }} className="flex items-center gap-2 px-4 py-2 border border-white/20 rounded-lg text-white hover:border-white/30 transition-all bg-white/5">
                  <Calendar size={18} className="text-white/80" /><span className="text-white font-medium text-sm">{p('due_date_filter')}</span><ChevronDown size={14} className={`text-white/70 transition-transform ${isDateRangeOpen ? 'rotate-180' : ''}`} />
                </button>
                {isDateRangeOpen && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[320px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 99999, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))', boxShadow: '0 8px 32px rgba(59, 130, 246, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.2)', overflow: 'visible' }}>
                    <div className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('date_start')}</label>
                          <DatePicker selected={dateRange.start} onChange={(date) => setDateRange({ ...dateRange, start: date })} dateFormat="dd/MM/yyyy" placeholderText={p('select_placeholder')} locale={datePickerLocale} className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 gps-datepicker" calendarClassName="gps-calendar" wrapperClassName="w-full" portalId="qawaem-datepicker-portal" popperClassName="date-picker-popper-high-z" />
                        </div>
                        <div>
                          <label className="text-white/80 text-xs mb-1.5 block w-full font-medium text-right">{p('date_end')}</label>
                          <DatePicker selected={dateRange.end} onChange={(date) => setDateRange({ ...dateRange, end: date })} dateFormat="dd/MM/yyyy" placeholderText={p('select_placeholder')} minDate={dateRange.start} locale={datePickerLocale} className="w-full px-3 py-2 bg-white/10 border border-blue-400/30 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400/50 gps-datepicker" calendarClassName="gps-calendar" wrapperClassName="w-full" portalId="qawaem-datepicker-portal" popperClassName="date-picker-popper-high-z" />
                        </div>
                      </div>
                      <div className="flex gap-2 pt-2 border-t border-white/10 mt-3">
                        <button type="button" onClick={() => setDateRange({ start: null, end: null })} className="flex-1 px-3 py-2 bg-white/5 hover:bg-white/10 border border-white/20 rounded-xl text-white/80 text-sm font-medium transition-all">{p('reset')}</button>
                        <button type="button" onClick={() => setIsDateRangeOpen(false)} className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl text-sm font-medium">{p('apply')}</button>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {selectedRecords.length > 0 && (
                <button type="button" onClick={handleBulkDelete} className="flex items-center gap-2 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 border border-red-500/30 text-red-200 rounded-lg transition-all text-sm font-medium">
                  <Trash2 size={16} /><span className="hidden sm:inline">{p('delete_count', { count: selectedRecords.length })}</span>
                </button>
              )}

              <div className="relative z-[100001]" ref={importDropdownRef}>
                <button type="button" onClick={() => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); }} className="bg-orange-500/20 hover:bg-orange-500/30 text-orange-200 border border-orange-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Upload size={16} /><span className="text-sm font-medium">{p('import_label')}</span><ChevronDown size={14} className={`transition-transform ${showImportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showImportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button type="button" onClick={() => { triggerFileInput(async (file) => {
                        const result = await importFromFile(file, exportHeaders, {});
                        if (result?.success && result.data?.length) {
                          for (const row of result.data) {
                            await localApiAdapter.post('/facturation/impayes-creances', {
                              ...row,
                              status: row.status || 'ouvert',
                            });
                          }
                          notifyCreated(showNotify, entityLabel);
                        }
                        setShowImportDropdown(false);
                        loadDossiers();
                      }, '.csv'); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{p('csv')}</span></button>
                      <button type="button" onClick={() => { triggerFileInput(async (file) => {
                        const result = await importFromFile(file, exportHeaders, {});
                        if (result?.success && result.data?.length) {
                          for (const row of result.data) {
                            await localApiAdapter.post('/facturation/impayes-creances', {
                              ...row,
                              status: row.status || 'ouvert',
                            });
                          }
                          notifyCreated(showNotify, entityLabel);
                        }
                        setShowImportDropdown(false);
                        loadDossiers();
                      }, '.xlsx,.xls'); }} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{p('excel')}</span></button>
                    </div>
                  </div>
                )}
              </div>

              <div className="relative z-[100001]" ref={exportDropdownRef}>
                <button type="button" onClick={() => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); }} className="bg-green-500/20 hover:bg-green-500/30 text-green-200 border border-green-500/30 px-3 py-2 rounded-lg transition-all duration-200 flex items-center gap-1.5">
                  <Download size={16} /><span className="text-sm font-medium">{p('export_label')}</span><ChevronDown size={14} className={`transition-transform ${showExportDropdown ? 'rotate-180' : ''}`} />
                </button>
                {showExportDropdown && (
                  <div className="border border-blue-400/60 rounded-2xl shadow-2xl min-w-[160px]" style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 100002, backgroundImage: 'linear-gradient(to bottom right, rgb(51, 65, 85), rgb(29, 71, 196), rgb(51, 65, 85))' }}>
                    <div className="p-2">
                      <button type="button" onClick={handleExportPDF} disabled={isGeneratingPDF} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3 disabled:opacity-50"><FileText size={16} className="text-red-400" /><span className="text-sm font-medium">{isGeneratingPDF ? p('generating') : p('pdf')}</span></button>
                      <button type="button" onClick={handleExportExcel} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3"><FileSpreadsheet size={16} className="text-green-400" /><span className="text-sm font-medium">{p('excel')}</span></button>
                      <button type="button" onClick={handleExportCSV} className="w-full px-4 py-2.5 text-left text-white/90 hover:bg-blue-500/20 rounded-xl flex items-center gap-3"><FileOutput size={16} className="text-purple-400" /><span className="text-sm font-medium">{p('csv')}</span></button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      filterActions={selectedRecords.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
        >
          <Trash2 size={16} />
          <span>{p('delete_count', { count: selectedRecords.length })}</span>
        </button>
      ) : null}
    >
{viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className={facturationTableHeadClass(qawaem)}>
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {showCol('reference_number') ? <TableColumnHeaderFilter columnKey="reference_number" label={p('col_reference')} value={filters.reference_number || ''} onChange={(v) => setFilter('reference_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('client_name') ? <TableColumnHeaderFilter columnKey="client_name" label={p('col_client')} value={filters.client_name || ''} onChange={(v) => setFilter('client_name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('invoice_number') ? <TableColumnHeaderFilter columnKey="invoice_number" label={p('col_invoice')} value={filters.invoice_number || ''} onChange={(v) => setFilter('invoice_number', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('amount_due') ? <TableColumnHeaderFilter columnKey="amount_due" label={p('col_amount_due')} value={filters.amount_due || ''} onChange={(v) => setFilter('amount_due', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('due_date') ? <TableColumnHeaderFilter columnKey="due_date" label={p('col_due_date')} value={filters.due_date || ''} onChange={(v) => setFilter('due_date', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white font-semibold py-4" /> : null}
                  {showCol('days_overdue') ? <TableColumnHeaderFilter columnKey="days_overdue" label={p('col_overdue')} value={filters.days_overdue || ''} onChange={(v) => setFilter('days_overdue', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <th className="px-6 py-4 text-center text-white font-semibold">{p('col_aging')}</th>
                  {showCol('status') ? <TableColumnHeaderFilter columnKey="status" label={p('col_status')} kind="select" options={statusOptions.map((o) => ({ value: o.value, label: o.label }))} value={filters.status || ''} onChange={(v) => setFilter('status', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} align="center" thClassName="text-white font-semibold py-4" /> : null}
                  <QawaemTableHeaderActionsCell
                    label={p('col_actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={visibleColumnCount}
                    totalCount={qawaemColumns.length}
                  thClassName="px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredDossiers.length > 0 ? (
                  pagedItems.map((record, index) => (
                    <tr key={record.id} className={`hover:bg-white/5 transition-colors ${selectedRecords.includes(record.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedRecords.includes(record.id)}
                            onChange={() => handleSelectRecord(record.id)}
                          />
                        </div>
                      </td>
                      {showCol('reference_number') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{record.reference_number}</span></td>
                      ) : null}
                      {showCol('client_name') ? (
                        <td className="px-6 py-4">
                          <ImpayeEditableCell recordId={record.id} field="client_name" value={record.client_name} className="text-white font-semibold" isEditing={editingCell?.recordId === record.id && editingCell?.field === 'client_name'} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={p('click_to_edit')} />
                        </td>
                      ) : null}
                      {showCol('invoice_number') ? (
                        <td className="px-6 py-4">
                          <ImpayeEditableCell recordId={record.id} field="invoice_number" value={record.invoice_number} className="text-white/70" isEditing={editingCell?.recordId === record.id && editingCell?.field === 'invoice_number'} editValue={editValue} onEditValueChange={setEditValue} onStartEdit={startEditing} onSave={saveInlineEdit} onCancel={cancelEditing} clickToEditTitle={p('click_to_edit')} />
                        </td>
                      ) : null}
                      {showCol('amount_due') ? (
                        <td className="px-6 py-4">
                          <span className="text-white font-bold">{formatBrandMoney(record.amount_due || 0)}</span>
                        </td>
                      ) : null}
                      {showCol('due_date') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{fmtDate(record.due_date)}</span></td>
                      ) : null}
                      {showCol('days_overdue') ? (
                        <td className="px-6 py-4 text-center"><div className="flex justify-center">{getOverdueBadge(record.days_overdue)}</div></td>
                      ) : null}
                      <td className="px-6 py-4 text-center"><div className="flex justify-center">{getAgingBadge(record.aging_bucket)}</div></td>
                      {showCol('status') ? (
                        <td className="px-6 py-4 text-center"><div className="flex justify-center"><QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} /></div></td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                          viewTitle={p('action_view') || p('view') || t('common.view')}
                          editTitle={p('action_edit') || p('edit') || t('common.edit')}
                          deleteTitle={p('action_delete') || p('delete') || t('common.delete')}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={visibleTableColCount} className="text-center py-12 text-white/60"><AlertCircle size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={visibleTableColCount} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm flex-wrap gap-2">
                      <span>{p('footer_total', { count: filteredDossiers.length })}</span>
                      <span>{p('footer_clients')}: <span className="text-white font-semibold">{dossiersFooter.nbClients}</span></span>
                      <span>{p('footer_amount_due')}: <span className="text-white font-semibold">{formatBrandMoney(dossiersFooter.montantDu)}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4 rounded-b-3xl">
            {filteredDossiers.length > 0 ? (
              pagedItems.map((record, index) => (
                <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-4 mb-4 flex-wrap gap-y-2">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><AlertCircle size={20} className="text-red-400" />{record.reference_number}</h3>
                        <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status')} />
                        {getAgingBadge(record.aging_bucket)}
                        {getOverdueBadge(record.days_overdue)}
                      </div>
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm text-white/70">
                        <div><span className="font-medium text-white">{p('card_client')}:</span> {record.client_name}</div>
                        <div><span className="font-medium text-white">{p('card_invoice')}:</span> {record.invoice_number || '—'}</div>
                        <div><span className="font-medium text-white">{p('card_amount_due')}:</span> {formatBrandMoney(record.amount_due || 0)}</div>
                        <div><span className="font-medium text-white">{p('card_due_date')}:</span> {fmtDate(record.due_date)}</div>
                        <div><span className="font-medium text-white">{p('card_last_reminder')}:</span> {fmtDate(record.last_reminder_date)}</div>
                        <div><span className="font-medium text-white">{p('card_notes')}:</span> {record.notes || '—'}</div>
                      </div>
                    </div>
                    <QawaemErpRowActions
                      compact
                      size={14}
                      onView={() => { setViewingRecord(record); setShowViewModal(true); }}
                      onEdit={() => handleEdit(record)}
                      onDelete={() => handleDelete(record.id)}
                      viewTitle={p('view')}
                      editTitle={p('edit')}
                      deleteTitle={p('delete')}
                    />
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="col-span-full text-center py-12"><AlertCircle size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty')}</p></div>
            )}
          </div>
        )}

      </FacturationListPageLayout>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
          open
          onClose={() => setShowViewModal(false)}
          title={p('view_title')}
          icon={Eye}
          maxWidth="1100px"
          testId="impaye-view-modal"
        >
              <div className={`${IMPAYE_SECTION} p-5`}>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div><p className="text-gray-500 text-sm mb-1">{p('label_reference')}</p><p className="text-gray-900 font-medium">{viewingRecord.reference_number}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_client')}</p><p className="text-gray-900 font-medium">{viewingRecord.client_name}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_invoice')}</p><p className="text-gray-900 font-medium">{viewingRecord.invoice_number || '—'}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_amount_due')}</p><p className="text-gray-900 font-medium">{formatBrandMoney(viewingRecord.amount_due || 0)}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_due_date')}</p><p className="text-gray-900 font-medium">{fmtDate(viewingRecord.due_date)}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('label_overdue_days')}</p><p className="text-gray-900 font-medium">{p('label_days_unit', { count: viewingRecord.days_overdue || 0 })}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_aging')}</p><p className="text-gray-900 font-medium">{viewingRecord.aging_bucket || '—'}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('col_status')}</p><p className="text-gray-900 font-medium">{getStatusLabel(viewingRecord.status) || '—'}</p></div>
                  <div><p className="text-gray-500 text-sm mb-1">{p('label_last_reminder')}</p><p className="text-gray-900 font-medium">{fmtDate(viewingRecord.last_reminder_date)}</p></div>
                  {viewingRecord.notes && <div className="md:col-span-3"><p className="text-gray-500 text-sm mb-1">{p('card_notes')}</p><p className="text-gray-900 font-medium">{viewingRecord.notes}</p></div>}
                </div>
              </div>
        </QawaemErpViewModal>
      ) : null}

      {showModal && (
        <QawaemErpCrudModal
          open={showModal}
          onClose={resetForm}
          title={editingRecord ? p('modal_edit') : p('modal_create')}
          icon={AlertCircle}
          formId="impaye-form"
          saveLabel={editingRecord ? p('update') : p('create')}
          onSubmit
          testId="impaye-crud-modal"
          tabs={dossierFormTabs}
          activeTab={dossierFormTab}
          onTabChange={setDossierFormTab}
        >
              <form id="impaye-form" onSubmit={handleSubmit}>
                {dossierFormTab === 'dossier' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_reference')}</label>
                            <input type="text" value={formData.reference_number} onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_reference')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right"><RequiredLabelText text={p('label_client_required')} /></label>
                            <input type="text" required value={formData.client_name} onChange={(e) => setFormData({ ...formData, client_name: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_client_name')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_client_id')}</label>
                            <input type="text" value={formData.client_id} onChange={(e) => setFormData({ ...formData, client_id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_client_id')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_amount_due')}</label>
                            <CustomNumberInput value={formData.amount_due} onChange={(v) => setFormData({ ...formData, amount_due: v })} placeholder={p('placeholder_amount')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('col_status')}</label>
                            <CustomSelect value={formData.status} onChange={(value) => setFormData({ ...formData, status: value })} options={statusOptions.map((s) => ({ value: s.value, label: s.label, isDefault: true }))} placeholder={p('select_status')} searchable autoSelect={false} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_invoice_number')}</label>
                            <input type="text" value={formData.invoice_number} onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_invoice_number')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_invoice_id')}</label>
                            <input type="text" value={formData.invoice_id} onChange={(e) => setFormData({ ...formData, invoice_id: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500" placeholder={p('placeholder_invoice_id')} />
                          </div>
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_due_date')}</label>
                            <input type="date" value={formData.due_date} onChange={(e) => setFormData({ ...formData, due_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                        </div>
                )}

                {dossierFormTab === 'followup' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_last_reminder')}</label>
                            <input type="date" value={formData.last_reminder_date} onChange={(e) => setFormData({ ...formData, last_reminder_date: e.target.value })} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          </div>
                          <div className="md:col-span-2">
                            <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('card_notes')}</label>
                            <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" placeholder={p('placeholder_notes')} />
                          </div>
                        </div>
                )}
              </form>
        </QawaemErpCrudModal>
      )}
    </div>
  );
};

export default ImpayesCreancesPage;

